import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";

interface HullPaintsFilterSelectProps {
  label: string;
  onChange: (values: string[]) => void;
  options: string[];
  selectedValues: string[];
  testId: string;
}

const HullPaintsFilterSelect = ({
  label,
  onChange,
  options,
  selectedValues,
  testId,
}: HullPaintsFilterSelectProps) => {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summary = selectedValues.length === 0
    ? "All"
    : selectedValues.length === 1
      ? selectedValues[0]
      : `${selectedValues.length} selected`;

  useEffect(() => {
    const closeWhenFocusLeaves = (event: PointerEvent) => {
      if (!detailsRef.current?.contains(event.target as Node)) {
        detailsRef.current?.removeAttribute("open");
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && detailsRef.current?.open) {
        detailsRef.current.removeAttribute("open");
        detailsRef.current.querySelector("summary")?.focus();
      }
    };

    document.addEventListener("pointerdown", closeWhenFocusLeaves);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeWhenFocusLeaves);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const toggleOption = (option: string) => {
    onChange(
      selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option],
    );
  };

  return (
    <div data-test={testId}>
      <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{label}</span>
      <details ref={detailsRef} className="group relative">
        <summary className="flex h-9 cursor-pointer list-none items-center justify-between rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 outline-none focus:border-green focus:ring-1 focus:ring-green dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 [&::-webkit-details-marker]:hidden">
          <span className="truncate">{summary}</span>
          <ChevronDownIcon className="h-4 w-4 flex-none transition-transform group-open:rotate-180" />
        </summary>
        <div className="absolute z-30 mt-1 max-h-64 w-full min-w-[190px] overflow-auto rounded-lg border border-gray-200 bg-white p-2 shadow-xl dark:border-gray-600 dark:bg-gray-800">
          <button
            type="button"
            onClick={() => onChange([])}
            className="mb-1 w-full rounded px-2 py-1.5 text-left text-xs font-semibold text-green hover:bg-primary-10"
            data-test={`${testId}-clear`}
          >
            All
          </button>
          {options.map((option) => (
            <label key={option} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-primary-10 dark:hover:bg-teal-950/50">
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => toggleOption(option)}
                className="h-4 w-4 rounded border-gray-400 text-green focus:ring-green"
                data-test={`${testId}-option-${option}`}
              />
              <span className="truncate" title={option}>{option}</span>
            </label>
          ))}
          {options.length === 0 && <p className="px-2 py-3 text-center text-xs text-gray-500">No values</p>}
        </div>
      </details>
    </div>
  );
};

export default HullPaintsFilterSelect;
